import { useCallback, useMemo, useState } from 'react';

import { useMaterials } from '~/api/easy-estimator-api/materials-api/hooks/useMaterials';
import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useResetSteps } from '~/core/hooks/useResetSteps';
import type { MaterialModel } from '~/core/models';
import { useAppDispatch } from '~/redux/hooks';
import { updateSelectedColorId } from '~/redux/slices/color';
import { updateSelectedMaterial } from '~/redux/slices/material';
import { useDrawingShapeContext } from '~contexts';
import { MaterialCardData } from '~molecules/MaterialCard';
import {
  RemovedProduct,
  collectRemovedProducts,
} from '~organisms/ProductReplacementModal';

type OverwritingMaterial =
  | {
      id: MaterialModel['id'];
      name: string;
    }
  | undefined;

export const useMaterialPage = () => {
  const dispatch = useAppDispatch();
  const { isFetchingMaterials, materials } = useMaterials();
  const selectedMaterial = useSelectedMaterial();
  const materialsList: MaterialCardData[] = useMemo(
    () =>
      materials.map((material) => ({
        id: material.id,
        img: { src: material.imageUrl ?? '' },
        title: material.description ?? '',
        priceRange: {
          min: material.minPrice ?? 0,
          max: material.maxPrice ?? 0,
        },
        specifications: material.attributes.map((attr) => ({
          title: attr.description,
          rating: attr.score,
        })),
        qteGrpId: material.qteGrpID,
      })),
    [materials]
  );
  const {
    getters: { areThereShapesInTheDrawingArea },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();
  const { restoreDataWithoutAutoNavigation } = useResetSteps();
  const {
    replaceProductsWithEquivalents,
    resolveAndMakeProductMatchingSummary,
  } = useEquivalentProductMatching();
  const [overwritingMaterial, setOverwritingMaterial] =
    useState<OverwritingMaterial>();
  const [removedProducts, setRemovedProducts] = useState<RemovedProduct[]>([]);

  const changeSelectedMaterial = useCallback(
    (material?: MaterialModel) => {
      restoreDataWithoutAutoNavigation(() => {
        if (material) {
          dispatch(updateSelectedMaterial(material));
        }
      });
    },
    [dispatch, restoreDataWithoutAutoNavigation]
  );

  const closeModal = useCallback(() => {
    setOverwritingMaterial(undefined);
    setRemovedProducts([]);
  }, []);

  const overwriteSelectedMaterial = useCallback(
    async (overwritingMaterialId: MaterialModel['id']) => {
      const material = materials.find(({ id }) => id === overwritingMaterialId);

      if (material) {
        // Do product matching first to get the updated shapes and matched color
        const result = await replaceProductsWithEquivalents({ material });

        if (!result) {
          changeSelectedMaterial(material);
          closeModal();

          return;
        }

        const { shapes: updatedShapes, validatedParams } = result;

        // Update material and color together - this ensures color is preserved
        restoreDataWithoutAutoNavigation(() => {
          dispatch(updateSelectedMaterial(material));
          // Update the selected color to the matched color from the new material
          if (validatedParams.color) {
            dispatch(updateSelectedColorId(validatedParams.color.productId));
          }
        });

        // Re-set the shapes after material/color update to ensure they persist through any effects
        // Use a small delay to let any effects from material/color update complete
        if (updatedShapes && updatedShapes.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 10));
          updateDrawingShapes([...updatedShapes]);
        }
      } else {
        changeSelectedMaterial();
      }

      closeModal();
    },
    [
      materials,
      changeSelectedMaterial,
      closeModal,
      replaceProductsWithEquivalents,
      dispatch,
      restoreDataWithoutAutoNavigation,
      updateDrawingShapes,
    ]
  );

  const selectMaterialId = useCallback(
    async (materialId: string) => {
      const material = materials.find(({ id }) => id === materialId);

      if (material?.id === selectedMaterial?.id) {
        return;
      }

      if (!areThereShapesInTheDrawingArea) {
        changeSelectedMaterial(material);

        return;
      }

      const summary = await resolveAndMakeProductMatchingSummary({
        material,
      });
      const removedProducts = summary ? collectRemovedProducts(summary) : [];

      if (removedProducts.length > 0) {
        setRemovedProducts(removedProducts);
        setOverwritingMaterial({
          id: materialId,
          name: material?.description ?? '',
        });
      } else {
        overwriteSelectedMaterial(materialId);
      }
    },
    [
      materials,
      selectedMaterial?.id,
      areThereShapesInTheDrawingArea,
      resolveAndMakeProductMatchingSummary,
      changeSelectedMaterial,
      overwriteSelectedMaterial,
    ]
  );

  return {
    isFetchingMaterials,
    materialsList,
    selectedMaterial,
    selectMaterialId,
    closeModal,
    overwritingMaterial,
    overwriteSelectedMaterial,
    removedProducts,
  };
};
